/* eslint-disable no-console */
import React from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import { withUserLayout } from 'ui/components/layouts';
import { Title, Container, Label } from 'ui/components/styled';
import { IconButton, ViewSwitcher } from 'ui/components/shared';
import { ListView, CardView } from './views';
import { Statistics } from './statistics';
import { Timer } from './timer';
import { ArchiveModal, ReorderModal } from './modals';
import { DashboardProvider } from 'providers';
import { useDashboard, useCompany, useUser, useOpenState, useWebSocket } from 'hooks';
import useMediaQuery from 'hooks/useMediaQuery';
import device from 'ui/theme/device';
import EmptyCompanies from './EmptyCompanies';

export const DashboardContainer = () => (
  <DashboardProvider>
    <DashboardContent />
  </DashboardProvider>
);

const DashboardContent = () => {
  const [view, setView] = React.useState('list');
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [openMobileFilter, setOpenMobileFilter] = React.useState(false);
  const [modal, openModal, closeModal] = useOpenState();
  const fullScreenHandle = useFullScreenHandle();
  const { stats, refreshStatistics } = useDashboard();
  const { subscribe, unsubscribe } = useWebSocket();
  const { isCompanyAdmin, activeCompany, companiesLoaded, companies } = useCompany();
  const { user } = useUser();
  const readonly = !isCompanyAdmin();
  const viewRef = React.useRef();
  const timerRef = React.useRef();
  const showTimer = React.useMemo(() => user?.data?.settings?.dashboard?.showTimer, [user]);

  const currentCompany = companies.find((company) => company.id === activeCompany);

  const currentCompanyName = currentCompany ? currentCompany.name : '';

  const isSmall = useMediaQuery(device.small);

  const refresh = (params) => {
    viewRef.current?.refresh(params);
  };

  const handleViewChange = (view) => {
    setView(view);
    refreshStatistics();
  };

  const handleFilterChange = (value) => {
    setSearch(value);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
  };

  const handleTimeEnd = () => {
    refresh({ silent: true });
  };

  const handleRealtimeUpdates = (params) => {
    // For now, no need to reset timer each time we receive realtime updates
    // since we already cancel any previous requests before performing new ones
    // timerRef.current?.resetTimer();
    viewRef.current?.handleRealtimeUpdates(params);
  };

  React.useEffect(() => {
    subscribe('jobs:update', handleRealtimeUpdates);

    return () => unsubscribe('jobs:update', handleRealtimeUpdates);
  }, []);

  const renderHeaderActions = (activeView = view) => (
    <>
      <ViewSwitcher active={activeView} onChange={handleViewChange} />
      {!readonly && (
        <>
          <IconButton
            title="Archive"
            icon="archive"
            iconProps={{ size: '24px' }}
            onClick={() => openModal('archive')}
            mr="xs"
          />
          <IconButton
            title="Category Reorder"
            icon="list-ordered"
            iconProps={{ size: '24px' }}
            onClick={() => openModal('reorder')}
            mr="xs"
          />
        </>
      )}
      {isSmall ? (
        <IconButton
          title="Reduce"
          icon="search"
          onClick={() => setOpenMobileFilter(!openMobileFilter)}
          mr="0"
        />
      ) : fullScreenHandle.active ? (
        <>
          <IconButton title="Reduce" icon="minimize" onClick={fullScreenHandle.exit} />
          <Statistics
            variant="small"
            search={search}
            activeStatus={status}
            onStatusChange={handleStatusChange}
            fetchOnFirstRender={false}
          />
        </>
      ) : (
        <IconButton title="Full screen" icon="maximize" onClick={fullScreenHandle.enter} />
      )}
    </>
  );

  if (companiesLoaded && companies.length === 0) {
    return <EmptyCompanies />;
  } else if (companiesLoaded && companies.length > 0) {
    return (
      <>
        <Container height="auto" px="m" pb="s">
          <Title variant="dark" mb="7px">
            Dashboard {isSmall && currentCompanyName && `(${currentCompanyName})`}
          </Title>
          <Container flexDirection="row">
            <Label flex="1" color="darkGrey" fontSize="15px" fontWeight="normal">
              Total: {stats?.total || 0}
            </Label>
            <Timer ref={timerRef} hidden={!showTimer} onTimeEnd={handleTimeEnd} />
          </Container>
        </Container>
        <Statistics search={search} activeStatus={status} onStatusChange={handleStatusChange} />
        <FullScreen handle={fullScreenHandle}>
          <Container id="dashboard-container" bg="#fff" pt="m">
            {view === 'list' ? (
              <ListView
                ref={viewRef}
                headerActions={renderHeaderActions()}
                isFullScreenActive={fullScreenHandle.active}
                openMobileFilter={openMobileFilter}
                filterValue={search}
                activeStatus={status}
                onFilterChange={handleFilterChange}
              />
            ) : (
              <CardView
                ref={viewRef}
                headerActions={renderHeaderActions()}
                isFullScreenActive={fullScreenHandle.active}
                openMobileFilter={openMobileFilter}
                filterValue={search}
                activeStatus={status}
                onFilterChange={handleFilterChange}
              />
            )}
            <ArchiveModal show={modal.isOpen('archive')} onRefresh={refresh} onClose={closeModal} />
            <ReorderModal show={modal.isOpen('reorder')} onRefresh={refresh} onClose={closeModal} />
          </Container>
        </FullScreen>
      </>
    );
  } else {
    return null;
  }
};

export const Dashboard = withUserLayout(DashboardContainer);
